import { useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BUILDING_NAMES, DEFAULT_SCIENCES, RESOURCE_NAMES, TEAM_PLANET_MISSIONS, type FleetMissionType, type GalaxyPosition } from '@kaeliux/shared';
import { extractScienceLevels, resolveTeamPlanetShipUpgrades } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useMyAlliance } from '../hooks/useMyAlliance';
import { useMissionLocks } from '../hooks/useAccountStanding';
import { useStoreTeamPlanet, useTeamPlanet } from '../hooks/useTeamPlanet';
import { formatFullNumber } from '../utils/formatters';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { Panel } from '../components/ui/Panel';
import SendFleetForm from '../components/SendFleetForm';
import TeamPlanetFlightsPanel from '../components/teamPlanet/TeamPlanetFlightsPanel';

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

/** Parses the Galaxy map's `?target=g:s:p` deep link. Empty segments (`Number('')` is `0`, not `NaN`) and non-integers are rejected rather than silently coerced into a real (wrong) target. */
function parseTargetParam(raw: string | null): GalaxyPosition | undefined {
    const parts = (raw ?? '').split(':');
    if (parts.length !== 3) return undefined;
    const [galaxy, system, position] = parts.map((p) => (p.trim() === '' ? NaN : Number(p)));
    if (
        !Number.isInteger(galaxy) || galaxy! < 1
        || !Number.isInteger(system) || system! < 1
        || !Number.isInteger(position) || position! < 0
    ) {
        return undefined;
    }
    return { galaxy: galaxy!, system: system!, position: position! };
}

/**
 * Dispatch a fleet FROM the alliance's Team planet (issue #158) — Sun Attack,
 * Sun Garrison and Space Trip only. A separate route/page rather than reusing the normal
 * `/fleets` page's origin machinery: the Team planet is deliberately never
 * made the app-wide `activePlanetId` (`planetStore`/`useGameLoop` only ever
 * tick a NORMAL planet — a Team-planet-flagged id is unconditionally
 * rejected by every single-planet endpoint, see `advance.ts`'s `runAdvance`
 * doc comment).
 *
 * Leader-only, genuinely enforced server-side (not just by this page's own
 * gate): `sendFleet` resolves a Team-planet origin via the caller's CURRENT
 * alliance membership/role (`resolveTeamPlanetRef` + a `role === 'leader'`
 * check), never by whose `players/{uid}/planets/**` subtree the doc
 * physically sits in — which stays the CONVERTING leader's forever, even
 * after a leadership transfer (`Planet.allianceId`'s doc comment). Reads are
 * looser than dispatch: `getTeamPlanet` resolves for ANY alliance member
 * (see its own doc comment), which is why the Team planet screens show it to
 * every member but only link this page for the leader. Every member does
 * get the read-only flight coordinator panel here (issue #294 follow-up).
 */
export default function TeamPlanetFleetsPage() {
    const storeTeamPlanet = useStoreTeamPlanet();
    const { profile } = useAuthStore();
    const { sciences } = usePlayerStore();
    const { flightMultiplier } = useUniverseStore();

    // The Galaxy map's Sun Attack quick-link deep-links here with a target
    // Sun (`/team-planet/fleets?target=1:2:0`) — same convention as
    // FleetsPage's own `?mission=&target=` deep link.
    const [searchParams] = useSearchParams();
    const initialTarget = useRef(parseTargetParam(searchParams.get('target')));
    // `?mission=sunGarrison` from a held Sun's "Send garrison" links.
    const initialMission = useRef<FleetMissionType>((() => {
        const raw = searchParams.get('mission') as FleetMissionType | null;
        return raw && TEAM_PLANET_MISSIONS.includes(raw) ? raw : 'sunAttack';
    })());
    const missionLocks = useMissionLocks(TEAM_PLANET_MISSIONS);

    const allianceQuery = useMyAlliance();
    const viewerRole = allianceQuery.data?.viewerRole ?? null;
    const isLeader = viewerRole === 'leader';

    // Every member reads it (the HUD already fetches the same cache entry on
    // this route); only the leader's dispatch form needs it polled.
    const teamPlanetQuery = useTeamPlanet({ enabled: !!allianceQuery.data, refetchInterval: isLeader ? 15_000 : undefined });

    if (allianceQuery.isPending || (isLeader && teamPlanetQuery.isPending)) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <div className="text-content-muted text-title">Loading…</div>
            </div>
        );
    }

    if (allianceQuery.isError || teamPlanetQuery.isError) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <p className="text-status-danger">
                    {errorMessage(allianceQuery.error ?? teamPlanetQuery.error, 'Failed to load the Team planet.')}
                </p>
            </div>
        );
    }

    if (!isLeader) {
        return (
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-8 flex flex-col gap-4">
                <h1 className="text-display text-content-primary">Team Planet Fleet</h1>
                <Panel variant="solid" padding="md">
                    <p className="text-content-muted text-label">
                        {allianceQuery.data
                            ? "Only your alliance's leader may dispatch fleets from the Team planet."
                            : "Only an alliance's leader may dispatch fleets from its Team planet — join or found an alliance first."}
                    </p>
                </Panel>
                {teamPlanetQuery.data && <TeamPlanetFlightsPanel />}
                <Link to="/alliance" className={linkButtonClasses('secondary', 'sm')}>← Back to Alliance</Link>
            </main>
        );
    }

    const teamPlanet = teamPlanetQuery.data ?? null;
    if (!teamPlanet) {
        return (
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-8 flex flex-col gap-4">
                <h1 className="text-display text-content-primary">Team Planet Fleet</h1>
                <Panel variant="solid" padding="md">
                    <p className="text-content-muted text-label">
                        Your alliance doesn&apos;t have a Team planet yet — convert one of your planets from its own
                        page first.
                    </p>
                </Panel>
                <Link to="/alliance" className={linkButtonClasses('secondary', 'sm')}>← Back to Alliance</Link>
            </main>
        );
    }

    if (!profile?.race || sciences === null) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <div className="text-content-muted text-title">Loading…</div>
            </div>
        );
    }

    return (
        <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-4 flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-display text-content-primary">Team Planet Fleet</h1>
                <div className="flex items-center gap-3">
                    <Link to="/fleets" className={linkButtonClasses('secondary', 'sm')}>Active Fleets</Link>
                    <Link to="/team-planet" className={linkButtonClasses('secondary', 'sm')}>← Back to Team Planet</Link>
                </div>
            </div>
            <p className="text-content-muted text-label">
                Dispatched from your Team planet at {teamPlanet.coordinates.galaxy}:{teamPlanet.coordinates.system}:{teamPlanet.coordinates.position} —
                only Sun Attack and Space Trip are available here.
            </p>
            <p className="text-content-muted text-label">
                {RESOURCE_NAMES.metal}: {formatFullNumber(Math.floor(teamPlanet.resources.metal.current))} · {RESOURCE_NAMES.minerals}:{' '}
                {formatFullNumber(Math.floor(teamPlanet.resources.minerals.current))} · {RESOURCE_NAMES.gas}:{' '}
                {formatFullNumber(Math.floor(teamPlanet.resources.gas.current))}
            </p>

            <SendFleetForm
                planet={teamPlanet}
                race={profile.race}
                // The Team planet's OWN collective sciences (drives,
                // Chemistry, Signal Systems) and ship upgrade levels for the
                // leader's race — never the leader's personal ones, same rule
                // `sendFleet`'s Team-planet-origin path enforces server-side
                // (issues #158, #352).
                sciences={extractScienceLevels(teamPlanet.sciences ?? DEFAULT_SCIENCES)}
                shipUpgrades={resolveTeamPlanetShipUpgrades(teamPlanet, profile.race)}
                flightMultiplier={flightMultiplier}
                allowedMissions={TEAM_PLANET_MISSIONS}
                missionLocks={missionLocks}
                initialMission={initialMission.current}
                initialTarget={initialTarget.current}
                emptyShipsMessage={`No ships docked at the Team planet. Build some in its ${BUILDING_NAMES.shipyard}.`}
                onSent={(result) => { storeTeamPlanet(result.planet).catch(() => { }); }}
            />

            <TeamPlanetFlightsPanel />
        </main>
    );
}
